<script setup lang="ts">
  import { computed, watch, onMounted } from 'vue';
  import useDocumentTypes from '../model/useDocumentTypes';
  import {
    useNavigationState,
    useNavigationTree,
    createNavigationCategories,
    DOCUMENT_TYPE_CATEGORY_ID,
    NavigationCategory,
    NavigationItem,
  } from '@/shared/lib';
  import { useModules } from '../model/useModules';
  import { useMatrixRouteNavigation } from '../model/useMatrixRouteNavigation';
  import { useModuleRouteNavigation } from '../model/useModuleRouteNavigation';

  import { useAuthStore } from '@/entities/Session';
  import { SimpleLoading } from '@/shared/ui';
  import NavigationContent from './NavigationContent.vue';
  import NavigationDropdown from '@/shared/ui/navigation/NavigationDropdown.vue';

  interface Props {
    isAuthenticated: boolean;
  }

  const props = defineProps<Props>();

  const {
    selectedCategory,
    selectedModules,
    selectedItemId: persistedSelectedItemId,
    expandedItems: persistedExpandedItems,
    clearAllState,
  } = useNavigationState(true);

  const {
    selectedItemId: treeSelectedItemId,
    expandedItems: treeExpandedItems,
    setSelectedItem,
    addExpandedItem,
    reset: resetTree,
  } = useNavigationTree({ useGlobalState: true });

  const {
    documentTypes,
    loading: documentTypesLoading,
    error: documentTypesError,
    fetchDocumentTypes,
  } = useDocumentTypes();
  const {
    modules,
    loading: modulesLoading,
    error: modulesError,
    fetchModules,
  } = useModules();
  const authStore = useAuthStore();
  const { syncFromRoute } = useMatrixRouteNavigation({
    selectedCategory,
    setSelectedItem,
    addExpandedItem,
  });
  const { syncFromRoute: syncModuleFromRoute } = useModuleRouteNavigation({
    selectedCategory,
    selectedModules,
    setSelectedItem,
    addExpandedItem,
  });

  onMounted(() => {
    if (persistedSelectedItemId.value) {
      setSelectedItem(persistedSelectedItemId.value);
    }
    persistedExpandedItems.value.forEach((id) => addExpandedItem(id));
    if (!selectedCategory.value) {
      selectedCategory.value = DOCUMENT_TYPE_CATEGORY_ID;
    }
    void syncFromRoute();
    void syncModuleFromRoute();
  });

  watch(
    () => treeSelectedItemId.value,
    (newId) => {
      persistedSelectedItemId.value = newId;
    }
  );

  watch(treeExpandedItems, (newSet) => {
    persistedExpandedItems.value = new Set(newSet);
  }, { deep: true });

  watch(selectedCategory, (_newCategory, oldCategory) => {
    if (oldCategory) {
      selectedModules.value = [];
    }
  });

  watch(
    () => authStore.selectedBranchId,
    (newBranchId, oldBranchId) => {
      if (newBranchId === oldBranchId) return;
      // The navigation mode survives the switch; only what belonged to the old branch is cleared,
      // so the module selector goes back to "Todos os módulos".
      const keptCategory = selectedCategory.value;
      resetTree();
      clearAllState();
      selectedCategory.value = keptCategory || DOCUMENT_TYPE_CATEGORY_ID;
      fetchDocumentTypes();
    }
  );

  const isDocumentTypeCategory = computed(() => selectedCategory.value === 'document');
  const isModuleCategory = computed(() => selectedCategory.value === 'module');
  const showTreeLoading = computed(
    () => isDocumentTypeCategory.value && documentTypesLoading.value
  );
  const showTreeError = computed(() => isDocumentTypeCategory.value && documentTypesError.value);
  // A tree already on screen stays while the Home revalidates it, so the spinner is only for the
  // first load.
  const showModulesLoading = computed(
    () => isModuleCategory.value && modulesLoading.value && modules.value.length === 0
  );
  const showModulesError = computed(
    () => isModuleCategory.value && !!modulesError.value && modules.value.length === 0
  );

  const retryModules = () => {
    fetchModules();
    fetchDocumentTypes();
  };

  const navigationCategories = computed<NavigationCategory[]>(() => {
    return createNavigationCategories(documentTypes.value, modules.value);
  });

  const emit = defineEmits<{
    'navigation-items-selected': [items: NavigationItem[]];
    'item-navigate': [item: NavigationItem];
  }>();

  const handleCategoryChange = (categoryId: string) => {
    selectedCategory.value = categoryId;
    selectedModules.value = [];
  };

  const handleModulesSelected = (modules: NavigationItem[]) => {
    selectedModules.value = modules;
    if (modules.length === 1) {
      emit('item-navigate', modules[0]);
    }
  };

  const handleItemsSelected = (items: NavigationItem[]) => {
    emit('navigation-items-selected', items);
  };

  const handleItemNavigate = (item: NavigationItem) => {
    emit('item-navigate', item);
  };

  const clearFilters = () => {
    selectedModules.value = [];
    clearAllState();
    selectedCategory.value = DOCUMENT_TYPE_CATEGORY_ID;
  };

  defineExpose({
    clearFilters,
  });
</script>

<template>
  <div
    class="flex flex-col h-full w-full bg-[var(--theme-primary)] border-r border-[var(--theme-secondary-ink)] divide-y divide-gray-02 text-sm/3"
  >
    <div class="pl-6 py-8 pr-5 space-y-3 flex-shrink-0">
      <NavigationDropdown
        v-model="selectedCategory"
        :categories="navigationCategories"
        :selected-modules="selectedModules"
        @category-change="handleCategoryChange"
        @modules-selected="handleModulesSelected"
      />
    </div>
    <div class="flex-1 overflow-hidden">
      <div
        v-if="showTreeLoading"
        class="flex justify-center px-4 py-6"
        data-testid="document-types-loading"
      >
        <SimpleLoading />
      </div>
      <div
        v-else-if="showTreeError"
        class="flex flex-col items-start gap-3 px-4 py-6 text-left"
        data-testid="document-types-error"
      >
        <p class="text-sm text-[var(--theme-text)]">
          Não foi possível carregar as pastas.
        </p>
        <button
          type="button"
          class="rounded bg-[var(--theme-secondary)] px-4 py-2 text-sm text-white hover:opacity-90"
          data-testid="document-types-retry"
          @click="fetchDocumentTypes"
        >
          Tentar novamente
        </button>
      </div>
      <div
        v-else-if="showModulesLoading"
        class="flex justify-center px-4 py-6"
        data-testid="modules-loading"
      >
        <SimpleLoading />
      </div>
      <div
        v-else-if="showModulesError"
        class="flex flex-col items-start gap-3 px-4 py-6 text-left"
        data-testid="modules-error"
      >
        <p class="text-sm text-[var(--theme-text)]">
          Não foi possível carregar os módulos.
        </p>
        <button
          type="button"
          class="rounded bg-[var(--theme-secondary)] px-4 py-2 text-sm text-white hover:opacity-90"
          data-testid="modules-retry"
          @click="retryModules"
        >
          Tentar novamente
        </button>
      </div>
      <NavigationContent
        v-else
        :selected-category="selectedCategory"
        :selected-modules="selectedModules"
        :categories="navigationCategories"
        @items-selected="handleItemsSelected"
        @item-navigate="handleItemNavigate"
      />
    </div>
  </div>
</template>
